<script lang="ts">
	import { onMount } from "svelte";
	import { slide } from "svelte/transition";
	import { yyyy, goto } from "./store.svelte";
	import Fa from "svelte-fa";
	import {
		faChevronDown,
		faChevronRight,
		faCircleNotch,
		faTrash,
		faMagnifyingGlass,
		faPlus,
		faLayerGroup,
	} from "@fortawesome/free-solid-svg-icons";
	import ScopeUpload from "./ScopeUpload.svelte";
	import ScopeLibrary from "./ScopeLibrary.svelte";

	let scopes = $state([]);
	let expandedScope = $state<string | null>(null);
	let loading = $state(true);
	let error = $state<string | null>(null);
	let ready = $state(false);
	let searchTerm = $state("");
	let showUpload = $state(false);
	let showLibrary = $state(false);
	let deleting = $state<string | null>(null);
	let confirmDelete = $state<{ id: string; name: string } | null>(null);
	let deleteError = "";

	const filteredScopes = $derived(
		scopes.filter((s: any) =>
			s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			(s.category || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
			(s.source || "").toLowerCase().includes(searchTerm.toLowerCase())
		)
	);

	const toggleScope = (id: string) => {
		expandedScope = expandedScope === id ? null : id;
	};

	const fetchScopes = async () => {
		loading = true;
		error = null;

		try {
			if (!yyyy.sesh.logtoken) {
				goto("/");
				return;
			}

			const response = await fetch(`${yyyy.uri.api}/scopes`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + yyyy.sesh.logtoken.token,
				},
			});

			if (!response.ok) {
				throw new Error(`Error fetching scopes: ${response.statusText}`);
			}

			scopes = await response.json();
		} catch (err: any) {
			console.error("Failed to fetch scopes:", err);
			error = err.message;
		} finally {
			loading = false;
		}
	};

	const requestDelete = (scope: any) => {
		deleteError = "";
		confirmDelete = { id: scope.id, name: scope.name };
	};

	const cancelDelete = () => {
		confirmDelete = null;
		deleteError = "";
	};

	const dropScope = async () => {
		if (!confirmDelete) return;
		deleting = confirmDelete.id;
		deleteError = "";

		try {
			const response = await fetch(`${yyyy.uri.api}/rpc/scope_drop`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + yyyy.sesh.logtoken.token,
				},
				body: JSON.stringify({ scope_id: confirmDelete.id }),
			});

			if (!response.ok) {
				throw new Error("Failed to delete scope");
			}

			scopes = scopes.filter((s: any) => s.id !== confirmDelete!.id);
			expandedScope = null;
			confirmDelete = null;
		} catch (err: any) {
			console.error("Failed to delete scope:", err);
			deleteError = err.message;
		} finally {
			deleting = null;
		}
	};

	const formatDate = (d: string) => {
		return new Date(d).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const onUploadComplete = () => {
		showUpload = false;
		fetchScopes();
	};

	const onLibraryComplete = () => {
		showLibrary = false;
		fetchScopes();
	};

	onMount(() => {
		if (!yyyy.sesh.logtoken) {
			goto("/");
			return;
		}

		setTimeout(() => {
			ready = true;
		}, 400);

		fetchScopes();
	});
</script>

<div class="scope-manager-container">
	<div class="scope-manager-content">
		<h1 class="title thin lvwr-title" class:dim={!ready} class:bri={ready}>
			Scope Manager
		</h1>
		<h2 class="subtitle lvwr-subtitle" class:dim={!ready} class:bri={ready}>
			Upload and manage spatial scope layers
		</h2>

		<div class="content lvwr-content" class:dim={!ready} class:bri={ready}>
			{#if showUpload}
				<ScopeUpload onComplete={onUploadComplete} onCancel={() => (showUpload = false)} />
			{:else if showLibrary}
				<ScopeLibrary onComplete={onLibraryComplete} onCancel={() => (showLibrary = false)} />
			{:else}
				<div class="toolbar">
					<div class="search-box">
						<input
							type="search"
							bind:value={searchTerm}
							placeholder="Search scopes..."
							class="search-input"
						/>
						<div class="search-icon">
							<Fa icon={faMagnifyingGlass} />
						</div>
					</div>
					<button
						class="button lvwr-toggle-orange"
						onclick={() => (showUpload = true)}
					>
						<Fa icon={faPlus} />
						<span style="margin-left: 0.4rem;">Upload Scope</span>
					</button>
					<button
						class="button lvwr-toggle"
						onclick={() => (showLibrary = true)}
					>
						<Fa icon={faLayerGroup} />
						<span style="margin-left: 0.4rem;">Browse Library</span>
					</button>
				</div>

				<div class="scopes-content">
					{#if loading}
						<div class="loading-state">
							<Fa icon={faCircleNotch} spin size="2x" />
							<p>Loading scopes...</p>
						</div>
					{:else if error}
						<div class="error-state">
							<p>Error: {error}</p>
							<button onclick={fetchScopes} class="retry-button">Retry</button>
						</div>
					{:else if scopes.length === 0}
						<div class="empty-state">
							<Fa icon={faLayerGroup} size="2x" color="#ccc" />
							<p>No scopes uploaded yet</p>
							<p class="empty-hint">Upload a GeoJSON, Shapefile, KML, or GeoPackage to get started</p>
						</div>
					{:else if filteredScopes.length === 0}
						<div class="empty-state">No scopes match your search</div>
					{:else}
						<div class="scopes-list">
							{#each filteredScopes as scope (scope.id)}
								<div class="scope-item">
									<div class="scope-header" onclick={() => toggleScope(scope.id)}>
										<span class="expand-icon">
											<Fa icon={expandedScope === scope.id ? faChevronDown : faChevronRight} />
										</span>
										<span class="scope-name">{scope.name}</span>
										{#if scope.category}
											<span class="scope-badge">{scope.category}</span>
										{/if}
										<span class="scope-count">{scope.feature_count ?? 0} features</span>
										<span class="scope-date">{formatDate(scope.created_at)}</span>
									</div>

									{#if expandedScope === scope.id}
										<div class="scope-details" transition:slide={{ duration: 200 }}>
											{#if scope.description}
												<div class="detail-row">
													<span class="detail-label">Description</span>
													<span class="detail-value">{scope.description}</span>
												</div>
											{/if}
											{#if scope.source}
												<div class="detail-row">
													<span class="detail-label">Source</span>
													<span class="detail-value">{scope.source}</span>
												</div>
											{/if}
											<div class="detail-row">
												<span class="detail-label">Created by</span>
												<span class="detail-value">{scope.created_by}</span>
											</div>
											{#if scope.geom_type}
												<div class="detail-row">
													<span class="detail-label">Geometry</span>
													<span class="detail-value">{scope.geom_type}</span>
												</div>
											{/if}
											<div class="detail-actions">
												<button
													class="button is-small is-danger is-outlined"
													onclick={() => requestDelete(scope)}
													disabled={deleting === scope.id}
												>
													{#if deleting === scope.id}
														<Fa icon={faCircleNotch} spin />
													{:else}
														<Fa icon={faTrash} />
													{/if}
													<span style="margin-left: 0.3rem;">Delete</span>
												</button>
											</div>
										</div>
									{/if}
								</div>
							{/each}
						</div>
					{/if}
				</div>
			{/if}
		</div>

		{#if confirmDelete}
			<div class="dialog-overlay" onclick={cancelDelete}>
				<!-- svelte-ignore a11y_click_events_have_key_events -->
				<!-- svelte-ignore a11y_no_static_element_interactions -->
				<div class="dialog" onclick={(e) => e.stopPropagation()}>
					<h3 class="dialog-title">Delete Scope</h3>
					<p class="dialog-body">
						Permanently delete <strong>{confirmDelete.name}</strong> and all its feature data? This cannot be undone.
					</p>
					{#if deleteError}
						<p class="dialog-error">{deleteError}</p>
					{/if}
					<div class="dialog-actions">
						<button class="button" onclick={cancelDelete} disabled={!!deleting}>
							Cancel
						</button>
						<button
							class="button is-danger"
							onclick={dropScope}
							disabled={!!deleting}
						>
							{#if deleting}
								<Fa icon={faCircleNotch} spin />
								<span style="margin-left: 0.4rem;">Deleting...</span>
							{:else}
								Delete
							{/if}
						</button>
					</div>
				</div>
			</div>
		{/if}

		<footer class="card-footer" class:dim={!ready} class:bri={ready}>
			<div class="card-footer-item sign-in-up-footer">
				<button
					onclick={() => goto("/livewires")}
					class="button lvwr-toggle"
				>
					Back to Livewires
				</button>
			</div>
		</footer>
	</div>
</div>

<style>
	.scope-manager-container {
		position: fixed;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		margin: auto;
		width: 80%;
		max-width: 1200px;
		height: 80vh;
		padding: 0 1rem;
		z-index: 5;
	}

	.scope-manager-content {
		background-color: rgba(255, 255, 255, 0.95);
		backdrop-filter: blur(2px);
		border: 1px solid #dbdbdb;
		border-radius: 8px;
		padding: 2rem;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		height: 100%;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		position: relative;
	}

	.dim {
		opacity: 0%;
		transition: opacity 2.5s ease;
	}

	.bri {
		opacity: 100%;
		transition: opacity 2.5s ease;
	}

	.content.lvwr-content {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
	}

	.toolbar {
		display: flex;
		gap: 0.75rem;
		margin-bottom: 1rem;
		margin-top: 1rem;
		align-items: center;
	}

	.search-box {
		position: relative;
		flex: 1;
	}

	.search-input {
		width: 100%;
		padding: 0.6rem 2.5rem 0.6rem 1rem;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.9rem;
		transition: all 0.2s;
		background-color: rgba(255, 255, 255, 0.95);
		caret-color: #ff3e00;
	}

	.search-input:focus {
		outline: none;
		border-color: #ff3e00;
		box-shadow: 0 0 0 2px rgba(255, 62, 0, 0.1);
	}

	.search-input::placeholder {
		color: #999;
	}

	.search-icon {
		position: absolute;
		right: 0.75rem;
		top: 50%;
		transform: translateY(-50%);
		color: #999;
		pointer-events: none;
	}

	.scopes-content {
		flex: 1;
		overflow-y: auto;
		min-height: 0;
	}

	.loading-state,
	.empty-state,
	.error-state {
		text-align: center;
		padding: 2rem 1rem;
		color: #666;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
	}

	.empty-hint {
		font-size: 0.85rem;
		color: #999;
	}

	.error-state {
		color: #ff3860;
	}

	.retry-button {
		padding: 0.5rem 1rem;
		border: 1px solid #3273dc;
		background-color: #3273dc;
		color: white;
		border-radius: 4px;
		cursor: pointer;
		transition: all 0.2s;
	}

	.retry-button:hover {
		background-color: #2366d1;
	}

	.scopes-list {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.scope-item {
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		overflow: hidden;
		background-color: rgba(255, 255, 255, 0.9);
	}

	.scope-header {
		padding: 8px 10px;
		background-color: rgba(255, 255, 255, 0.95);
		display: flex;
		align-items: center;
		cursor: pointer;
		transition: all 0.2s;
		font-size: 0.85rem;
	}

	.scope-header:hover {
		background-color: rgba(255, 62, 0, 0.05);
	}

	.expand-icon {
		margin-right: 6px;
		width: 12px;
		color: #ff3e00;
	}

	.scope-name {
		font-weight: 500;
		color: #363636;
		margin-right: 8px;
		flex: 1;
		font-size: 0.85rem;
	}

	.scope-badge {
		background-color: #3273dc;
		color: white;
		border-radius: 10px;
		padding: 2px 8px;
		font-size: 0.7rem;
		margin-right: 8px;
		text-transform: capitalize;
	}

	.scope-count {
		color: #666;
		font-size: 0.75rem;
		margin-right: 8px;
	}

	.scope-date {
		color: #999;
		font-size: 0.7rem;
	}

	.scope-details {
		padding: 10px;
		background-color: rgba(255, 255, 255, 0.98);
		border-top: 1px solid #eee;
		font-size: 0.85rem;
	}

	.detail-row {
		display: flex;
		padding: 4px 0;
		border-bottom: 1px solid #f0f0f0;
	}

	.detail-row:last-of-type {
		border-bottom: none;
	}

	.detail-label {
		flex: 0 0 100px;
		font-weight: 500;
		color: #363636;
		font-size: 0.8rem;
	}

	.detail-value {
		flex: 1;
		color: #666;
		font-size: 0.8rem;
	}

	.detail-actions {
		margin-top: 8px;
		display: flex;
		justify-content: flex-end;
	}

	.dialog-overlay {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.35);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 20;
		border-radius: 8px;
	}

	.dialog {
		background: white;
		border-radius: 8px;
		padding: 1.5rem;
		width: 90%;
		max-width: 400px;
		box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
	}

	.dialog-title {
		font-size: 1rem;
		font-weight: 600;
		color: #363636;
		margin-bottom: 0.75rem;
	}

	.dialog-body {
		font-size: 0.9rem;
		color: #555;
		line-height: 1.5;
		margin-bottom: 1rem;
	}

	.dialog-error {
		color: #ff3860;
		font-size: 0.85rem;
		margin-bottom: 0.75rem;
	}

	.dialog-actions {
		display: flex;
		justify-content: flex-end;
		gap: 0.5rem;
	}
</style>
